import { Dispatch, SetStateAction, useState } from "react";
import ModalV2 from "../modal-v2";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { clientInitialPermissions, preview_image } from "@/constants";
import { HeadingText, StyledBox, StyledTextInput } from "./styles";
import SwitchButton from "@/components/switch";
import { MultiCheckbox, PartnerPicker, PictureUploader } from "@/components";
import { useAddClientMutation } from "@/services";
import { roles } from "@/roles";
import { useGetTenantAllowedPagesQuery, useSetUserAllowedPagesMutation } from "@/graphql/page-access";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useSnackbar, useUploadFile } from "@/hooks";
import { ValidationPatterns } from "@/utils/form-validation";
import { useAppDispatch, useAppSelector } from "@/store";
import { clientApi } from "@/services/client/client-api";

type AddClientModalProps = {
  show: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
  selectedPartner: SPOwnerPartnerTypes | null;
  setSelectedPartner: Dispatch<SetStateAction<SPOwnerPartnerTypes | null>>;
};

// Legacy arrays retained for now but UI will be PageKey-based; legacy toggles hidden

const AddClientModal = ({
  show,
  setShow,
  selectedPartner,
  setSelectedPartner,
}: AddClientModalProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<ClientFormValues>({
    defaultValues: {
      firstName: "",
      email: "",
      profileImage: null,
      assignedPartner: "",
    },
  });
  const [showPermissions, setShowPermissions] = useState(false);
  const [permissions, setPermissions] = useState(clientInitialPermissions);
  const [allowedPages, setAllowedPages] = useState<string[]>([]);
  const [profilePreview, setProfilePreview] = useState<string | undefined>(
    undefined
  );
  const { isLoading: profileImgLoading, uploadFile } = useUploadFile();
  const user = useAppSelector((state) => state.auth.user);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const isAdmin = user?.userRole === roles.TDADMIN;
  const isTDAdminSP = isAdmin && selectedTenant?.tenantType === "SERVICE_PROVIDER";
  const { data: tenantPagesResp } = useGetTenantAllowedPagesQuery(
    isTDAdminSP ? { tenantId: selectedTenant?._id } : undefined
  );
  const availablePageKeys = tenantPagesResp?.pages || [];
  const PAGE_OPTIONS: Array<{ key: string; label: string }> = [
    { key: "ANALYTICS", label: "Analytics" },
    { key: "POTENTIAL_SAVINGS", label: "Potential Savings" },
    { key: "SCENARIO_MODELING", label: "Scenario Modeling" },
    { key: "CHAT_ASSISTANCE", label: "Chat Assistance" },
    { key: "PRODUCT_HUB", label: "Product Hub" },
    { key: "TRADE_DATA", label: "Trade Data" },
    { key: "DOCUMENTS", label: "Documents" },
    { key: "POST_IMPORT_ADJUSTMENT", label: "Post Import Adjustment" },
  ];
  const filteredOptions = PAGE_OPTIONS.filter((o) => availablePageKeys.includes(o.key));
  const [setUserAllowedPages] = useSetUserAllowedPagesMutation();
  const handleTriggerPermissions = () => {
    setShowPermissions((prev) => !prev);
    setPermissions(clientInitialPermissions);
  };
  const { show: showSnackBar } = useSnackbar();

  const [addClient, { isLoading }] = useAddClientMutation();

  // Removed legacy permission handlers and DocumentAccess toggles from creation UI

  const handleProfileChange = (file: File | null) => {
    if (file) {
      const fileURL = URL.createObjectURL(file);
      setValue("profileImage", file);
      setProfilePreview(fileURL);
    }
  };

  const dispatch = useAppDispatch();
  const handleCreateClient: SubmitHandler<ClientFormValues> = async (data) => {
    const { firstName, assignedPartner, profileImage, email } = data;

    let profileImgKey;
    if (profileImage) {
      const data = await uploadFile(profileImage);
      profileImgKey = data?.key || "";
    }
    const clientData: ClientType = {
      firstName,
      email,
      profileImage: profileImgKey || "",
      isRestricted: !showPermissions,
      assignedPartner: assignedPartner ? assignedPartner : null,
      ...(isAdmin && { tenantId: selectedTenant?._id }),
      clientPermission: {
        dashboard: Object.keys(permissions.dashboard).filter(
          (key) =>
            permissions.dashboard[key as keyof typeof permissions.dashboard]
        ),
        documents: permissions.documents,
        reports: Object.keys(permissions.reports).filter(
          (key) => permissions.reports[key as keyof typeof permissions.reports]
        ),
      } as ClientAccessLevelPermissions,
    };

    try {
      await addClient({
        clientData,
      }).unwrap();
      // After create, try to find the new client by email and set their allowed pages
      const finalPages = (allowedPages || []).filter((k) => availablePageKeys.includes(k));
      if (finalPages.length > 0) {
        try {
          const resp = await dispatch(
            clientApi.endpoints.getAllClients.initiate({
              pageNo: 1,
              pageSize: 10,
              searchKeyword: email,
              ...(isAdmin && { tenantId: selectedTenant?._id }),
            })
          ).unwrap();
          const newClient = resp?.data?.find((c) => c.email === email);
          if (newClient?._id) {
            await setUserAllowedPages({ userId: newClient._id, pages: finalPages }).unwrap();
          }
        } catch (e) {
          try {
            showSnackBar("Client created, but assigning pages failed. You can update access in Settings.", "", "warning");
          } catch {}
        }
      }
      setSelectedPartner(null);
      showSnackBar("Client Created Successfully!", "", "success");
      setShow(false);
      reset();
    } catch (error) {
      if (error instanceof Error) {
        const errorMessage = error?.message;
        showSnackBar(errorMessage, "", "error");
      }
    }
  };

  return (
    <ModalV2
      show={show}
      setShow={setShow}
      buttonAction={handleSubmit(handleCreateClient)}
      isConfirmButtonLoading={isLoading || profileImgLoading}
      buttonText={"Invite"}
      title="Add New Client"
      subTitle="They'll be able to access Riffs and see their data once invited"
    >
      <Box component="form" onSubmit={handleSubmit(handleCreateClient)}>
        <Stack my={2}>
          <Typography
            sx={{ mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}
            fontWeight={500}
            fontSize={14}
            variant="caption"
          >
            Select Client
          </Typography>
          <Controller
            name="assignedPartner"
            control={control}
            render={({ field: { onChange, ...rest } }) => (
              <PartnerPicker
                {...rest}
                selectedPartner={selectedPartner}
                setSelectedPartner={(partner: SPOwnerPartnerTypes | null) => {
                  const partnerId = partner ? partner._id : "";
                  onChange(partnerId);
                  setSelectedPartner(partner);
                }}
                error={errors.assignedPartner}
              />
            )}
          />
          {errors.assignedPartner && (
            <Typography
              color="error"
              sx={{
                mt: 0.5,
                fontSize: "0.75rem",
                ml: 1,
              }}
            >
              {errors.assignedPartner.message}
            </Typography>
          )}
          <Divider sx={{ mt: 2 }} />
          <PictureUploader
            title="Logo"
            description="We recommend an image of at least 400x400"
            imgUrl={profilePreview ? profilePreview : preview_image}
            customPreviewType={profilePreview ? "rounded" : "default"}
            onChange={handleProfileChange}
          />
          <Controller
            name="firstName"
            control={control}
            rules={{ required: "Business name is required" }}
            render={({ field: { onChange, ...rest }, fieldState }) => (
              <StyledTextInput
                label="Business Name"
                type="text"
                {...rest}
                placeholder="Business Name"
                handleChangeValue={onChange}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            rules={{
              required: "Email is required",
              pattern: {
                value: ValidationPatterns.email,
                message: "Invalid email address",
              },
            }}
            render={({ field: { onChange, ...rest }, fieldState }) => (
              <StyledTextInput
                label="Email Address"
                {...rest}
                type="text"
                placeholder="h1@example.com"
                handleChangeValue={onChange}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Stack sx={{ border: "1px solid #e4e7ec" }}>
            <StyledBox
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <HeadingText>
                  Do you want your client to login to the system
                </HeadingText>

                <Typography
                  sx={{
                    color: "#8E9494",
                    fontSize: "12px",
                  }}
                >
                  This will provide your client a portal to login and manage
                  everything
                </Typography>
              </Box>
              <SwitchButton label="" onChange={handleTriggerPermissions} />
            </StyledBox>
            <Divider />
            {showPermissions && (
              <>
                <StyledBox>
                  <HeadingText>Pages</HeadingText>
                  <Typography sx={{ color: "#8E9494", fontSize: "12px" }}>
                    Toggle which app pages this client can access
                  </Typography>
                </StyledBox>
                <StyledBox>
                  <MultiCheckbox
                    label="Core Pages"
                    checkboxes={filteredOptions.map((o) => ({ label: o.label }))}
                    checked={filteredOptions.map((o) => allowedPages.includes(o.key))}
                    handleParentChange={(e) => {
                      const checked = e.target.checked;
                      if (checked) setAllowedPages(filteredOptions.map((o) => o.key));
                      else setAllowedPages([]);
                    }}
                    handleChildChange={(index) => () => {
                      const key = filteredOptions[index]?.key;
                      if (!key) return;
                      setAllowedPages((prev) =>
                        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
                      );
                    }}
                  />
                  {filteredOptions.length === 0 && (
                    <Typography sx={{ color: "#8E9494", fontSize: "12px", mt: 0.5 }}>
                      No tenant pages configured. Contact your administrator.
                    </Typography>
                  )}
                </StyledBox>
              </>
            )}
          </Stack>
        </Stack>
      </Box>
    </ModalV2>
  );
};

export default AddClientModal;
